<template>
  <div id="contenedor-default-obra">
    <v-skeleton-loader v-if="$apollo.loading" type="card"></v-skeleton-loader>
    <div v-else>
      <!-- INFORMACIÓN BÁSICA -->
      <v-card flat class="mb-4">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-information-outline</v-icon>
          Información Básica
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="3">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Clave</span>
              </div>
              <div>
                <span class="font-weight-medium text-body-1">{{ getObra.clave }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Ejercicio</span>
              </div>
              <div>
                <span class="font-weight-medium text-body-1">{{ getObra.ejercicio }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Tipo</span>
              </div>
              <div>
                <v-chip
                  small
                  :color="(getObra.tipo && getObra.tipo.color)
                    ? getObra.tipo.color
                    : 'grey'"
                  text-color="white">
                  {{ getObra.tipo ? getObra.tipo.descripcion : '' }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Subtipo</span>
              </div>
              <div>
                <v-chip
                  small
                  :color="(getObra.subtipo && getObra.subtipo.color)
                    ? getObra.subtipo.color
                    : 'grey'"
                  text-color="white">
                  {{ getObra.subtipo ? getObra.subtipo.descripcion : '' }}
                </v-chip>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Programa</span>
              </div>
              <div>
                <span class="text-uppercase font-weight-medium">
                  {{ getObra.programa ? getObra.programa.nombre : '' }}
                </span>
                <v-chip x-small class="ml-2" outlined>
                  {{ getObra.programa ? getObra.programa.clave : '' }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Etapa</span>
              </div>
              <div>
                <span class="font-weight-medium">{{ getEtapaTexto(getObra.etapa) }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Tipo Proyecto
                </span>
              </div>
              <div>
                <span class="font-weight-medium">{{ getTipoProyectoTexto(getObra.tipoProyecto) }}
                </span>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Objeto/Meta
                </span>
              </div>
              <div>
                <span class="text-uppercase">{{ getObra.meta }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- MONTOS -->
      <v-card flat class="mb-4">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-currency-usd</v-icon>
          Montos
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Importe</span>
              </div>
              <div>
                <span class="font-weight-medium text-h6">
                  {{ getObra.importe | currency('$', 2) }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Importe IVA
                </span>
              </div>
              <div>
                <span class="font-weight-medium text-h6">
                  {{ getObra.importeIva | currency('$', 2) }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Total</span>
              </div>
              <div>
                <span class="font-weight-bold text-h5 primary--text">
                  {{ getObra.total | currency('$', 2) }}
                </span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- UBICACIÓN -->
      <v-card flat class="mb-4">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-map-marker</v-icon>
          Ubicación
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Municipio</span>
              </div>
              <div>
                <span class="text-uppercase font-weight-medium">
                  {{
                    getObra.ubicacion
                    ? getObra.ubicacion.municipio
                    ? getObra.ubicacion.municipio.nombre : '' : ''
                  }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Localidad</span>
              </div>
              <div>
                <span>{{ getObra.ubicacion ? getObra.ubicacion.localidad : '' }}</span>
                <v-chip
                  v-if="getObra.ubicacion ? getObra.ubicacion.cabeceraMunicipial : false"
                  x-small
                  color="amber darken-2"
                  text-color="white"
                  class="ml-2">
                  Cabecera
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Tipo Localidad
                </span>
              </div>
              <div>
                <span>{{ getObra.ubicacion ? getObra.ubicacion.tipoLocalidad : '' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="getObra.ubicacion ? getObra.ubicacion.ubicacion : false">
            <v-col cols="12">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Descripción Ubicación</span>
              </div>
              <div>
                <span>{{ getObra.ubicacion ? getObra.ubicacion.ubicacion : '' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="getObra.ubicacion
          ? getObra.ubicacion.latitud && getObra.ubicacion.longitud : false">
            <v-col cols="12" md="6">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">Coordenadas
                </span>
              </div>
              <div>
                <v-chip small outlined class="mr-2">
                  <v-icon small class="mr-1">mdi-crosshairs-gps</v-icon>
                  {{ getObra.ubicacion.latitud }}, {{ getObra.ubicacion.longitud }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- CRONOGRAMA -->
      <v-card flat class="mb-4">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
          Cronograma
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Solicitud Recursos
                </span>
              </div>
              <div>
                <span v-if="getObra.fechaSolicitudRecursos" class="font-weight-medium">
                  {{ formatoFecha(getObra.fechaSolicitudRecursos) }}
                </span>
                <span v-else class="grey--text text--lighten-1">No especificado</span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Inicio Probable
                </span>
              </div>
              <div>
                <span v-if="getObra.fechaProbableInicio" class="font-weight-medium">
                  {{ formatoFecha(getObra.fechaProbableInicio) }}
                </span>
                <span v-else class="grey--text text--lighten-1">No especificado</span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Término Probable
                </span>
              </div>
              <div>
                <span v-if="getObra.fechaProbableTermino" class="font-weight-medium">
                  {{ formatoFecha(getObra.fechaProbableTermino) }}
                </span>
                <span v-else class="grey--text text--lighten-1">No especificado</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- IMPACTO Y AVANCES -->
      <v-card flat class="mb-4" v-if="getObra.impacto">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-chart-line</v-icon>
          Impacto y Avances
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Beneficiados
                </span>
              </div>
              <div>
                <span class="font-weight-medium text-h6">
                  {{ getObra.impacto.beneficiados || 0 }}
                </span>
                <span class="grey--text"> personas</span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Meta Física
                </span>
              </div>
              <div>
                <span class="font-weight-medium">
                  {{ getObra.impacto.metaFisica }} {{ getObra.impacto.metaFisicaUnidad }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Avance Actual
                </span>
              </div>
              <div>
                <v-progress-linear
                  :value="getObra.impacto.avanceFisicoActual || 0"
                  color="primary"
                  height="24"
                  rounded>
                  <span class="white--text font-weight-bold">
                    {{ getObra.impacto.avanceFisicoActual || 0 }}%
                  </span>
                </v-progress-linear>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="getObra.impacto.problematica">
            <v-col cols="12">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Problemática
                </span>
              </div>
              <div>
                <span>{{ getObra.impacto.problematica }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="getObra.impacto.justificacionTecnica">
            <v-col cols="12" md="6">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Justificación Técnica
                </span>
              </div>
              <div>
                <span>{{ getObra.impacto.justificacionTecnica }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="getObra.impacto.justificacionSocial">
              <div class="mb-1">
                <span class="grey--text text--darken-1 text-caption text-uppercase">
                  Justificación Social
                </span>
              </div>
              <div>
                <span>{{ getObra.impacto.justificacionSocial }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ETIQUETAS -->
      <v-card flat class="mb-4" v-if="getObra.etiquetas && getObra.etiquetas.length > 0">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-tag-multiple</v-icon>
          Etiquetas
        </v-card-title>
        <v-card-text class="pa-4">
          <div>
            <v-chip
              v-for="etiquetaItem in getObra.etiquetas"
              :key="etiquetaItem.idEtiqueta"
              :color="etiquetaItem.etiqueta.color"
              :text-color="etiquetaItem.etiqueta.esTextoClaro ? 'white' : 'black'"
              class="mr-2 mb-2"
              small>
              {{ etiquetaItem.etiqueta.nombre }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- RESPONSABLES -->
      <v-card flat class="mb-4" v-if="getObra.responsables && getObra.responsables.length > 0">
        <v-card-title class="pa-4 grey darken-3 white--text">
          <v-icon color="white" class="mr-2">mdi-account-group</v-icon>
          Responsables
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col
              v-for="responsable in getObra.responsables"
              :key="responsable.id"
              cols="12" md="6">
              <v-card outlined class="pa-0">
                <v-card-text class="pa-3">
                  <div class="font-weight-bold">{{ responsable.funcionario.nombre }}</div>
                  <div class="caption grey--text">{{ responsable.funcionario.puesto }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import OBRA from '@/graphql/querys/administracion/planeacion/obra.gql';

export default {
  name: 'DefaultObra',
  data() {
    return {
    };
  },
  apollo: {
    obra: {
      query: OBRA,
      variables() {
        return {
          id: this.$route.params.idObra,
        };
      },
      skip() {
        return !this.$route.params.idObra;
      },
    },
  },
  methods: {
    formatoFecha(fecha) {
      if (!fecha) return null;
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year}`;
    },
    getEtapaTexto(etapa) {
      const etapas = {
        1: 'Nueva',
        2: 'Ampliación',
        3: 'Rehabilitación',
      };
      return etapas[etapa] || 'No especificado';
    },
    getTipoProyectoTexto(tipoProyecto) {
      const tipos = {
        1: 'Social',
        2: 'Productivo',
        3: 'Apoyo',
      };
      return tipos[tipoProyecto] || 'No especificado';
    },
  },
  computed: {
    getObra() {
      if (!this.obra) return {};
      return this.obra;
    },
  },
};
</script>

<style lang="scss">
</style>
